/**
 * The apex domains tenant subdomains hang off — one spelling, shared by
 * everything that has to take a hostname apart or put one together.
 *
 * Two callers need the same vocabulary and used to keep private copies:
 * ``tenantSlugFromHostname`` (hostname → active org slug) and the
 * ``buildTenantUrl`` / ``buildPlatformUrl`` builders in ``resolveShell``.
 * The builders' copy was not a list at all but a literal ``forgmatic.com``
 * glued to a literal ``https://``, so on any other deployment — a dev
 * server above all — clicking "switch org" navigated the browser to
 * **production**.
 *
 * Both local apexes are in the default list because ``*.localhost`` is how
 * the tenant-per-subdomain model is exercised off a production DNS zone —
 * the dev stack, the Playwright suite (``playwright.config.ts`` sets this
 * same value explicitly) and any reviewer poking at a branch all live
 * there. Production hostnames never end in ``.localhost``, so carrying
 * them costs nothing there.
 *
 * 🚨 ``forgmatic.localhost`` is the apex to use when a local session has
 * to survive a tenant hop. A session cookie cannot be scoped to
 * ``localhost``: browsers and curl alike refuse ``Domain=localhost``
 * outright (single-label / public-suffix rule), so the cookie stays
 * host-only and every switch lands on a sign-in prompt. Two labels is
 * what makes ``COOKIE_DOMAIN`` legal, and Chrome resolves any
 * ``*.localhost`` name to loopback, so no DNS or hosts entry is needed.
 *
 * Comma-separated build-time override: ``VITE_TENANT_APEX_DOMAINS``.
 */
function parseApexList(raw: string): readonly string[] {
  return raw
    .split(",")
    .map((d) => d.trim().toLowerCase().replace(/^\.+|\.+$/g, ""))
    .filter((d) => d.length > 0)
    // Longest first: ``stark.forgmatic.localhost`` matches both
    // ``forgmatic.localhost`` and ``localhost``, and only the longer one
    // names the zone the cookie is scoped to. Shortest-first would build
    // ``tycho.localhost`` — a host the session cookie does not reach.
    .sort((a, b) => b.length - a.length);
}

const RAW_TENANT_APEX_DOMAINS: string =
  (import.meta.env?.VITE_TENANT_APEX_DOMAINS as string | undefined)?.trim() ||
  "forgmatic.com,forgmatic.cz,forgmatic.localhost,localhost";

/** Where TENANTS live. ``{slug}.{apex}`` is built on these. */
export const TENANT_APEX_DOMAINS: readonly string[] = parseApexList(
  RAW_TENANT_APEX_DOMAINS,
);

/**
 * First PRODUCTION (non-local) apex in the CONFIGURED order — the one
 * new tenants are promised at sign-up (KAN-40A). Order matters and is
 * the author's, not the length-sorted matching order above: the
 * operator puts the canonical zone first (``forgmatic.app,…`` after
 * the domain split), and the sign-up screen must show THAT, never a
 * hardcoded literal that silently drifts from where tenants really
 * live. Exported pure for tests.
 */
export function firstProductionApex(raw: string): string | null {
  return (
    raw
      .split(",")
      .map((d) => d.trim().toLowerCase().replace(/^\.+|\.+$/g, ""))
      .find((d) => d.length > 0 && !d.endsWith("localhost")) ?? null
  );
}

export function primaryTenantApex(): string {
  return firstProductionApex(RAW_TENANT_APEX_DOMAINS) ?? "forgmatic.com";
}

/**
 * Where the PLATFORM lives — marketing, sign-up, and the staff console.
 *
 * A separate list because these answer different questions, and they
 * stop having the same answer the moment tenants move to their own
 * registrable domain. Mirrors ``Settings.platform_domain`` vs
 * ``Settings.tenant_apex`` on the API; the two ends must agree or the
 * shell rendered and the tenant resolved disagree about the same URL.
 *
 * Defaults mirror the tenant list on the real apexes, so a deploy that
 * has not split its zones behaves exactly as before and the cutover is
 * an env change rather than a release.
 *
 * ⚠️ Bare ``localhost`` is deliberately ABSENT, though the tenant list
 * carries it. Dev defaults to the TENANT shell — that is where the bulk
 * of the local surface lives (cart, checkout, quote, admin) and what the
 * e2e suite drives. Adding it here turns every jsdom test and every
 * ``npm run dev`` session into the marketing site. ``forgmatic.localhost``
 * is the local platform apex; ``VITE_FORCE_SHELL`` is the override.
 *
 * Comma-separated build-time override: ``VITE_PLATFORM_APEX_DOMAINS``.
 */
export const PLATFORM_APEX_DOMAINS: readonly string[] = parseApexList(
  (import.meta.env?.VITE_PLATFORM_APEX_DOMAINS as string | undefined)?.trim() ||
    "forgmatic.com,forgmatic.cz,forgmatic.localhost",
);

/** The staff console lives at this label under a PLATFORM apex. */
export const PLATFORM_ADMIN_LABEL = "admin";

/**
 * Is ``hostname`` the cross-tenant staff console?
 *
 * 🚨 Exact match on ``admin.{platformApex}``, never a bare
 * ``startsWith("admin.")``. Once tenants have their own zone,
 * ``admin.{tenantApex}`` is a hostname inside the namespace strangers
 * provision themselves into — a prefix test would render the
 * cross-tenant staff shell there. ``admin`` is also a reserved slug on
 * the API, so this is the second of two independent guards; either one
 * alone is a single point of failure.
 */
export function isPlatformAdminHost(hostname: string): boolean {
  const host = normalizeHostname(hostname);
  return PLATFORM_APEX_DOMAINS.some(
    (apex) => host === `${PLATFORM_ADMIN_LABEL}.${apex}`,
  );
}

/** Normalise a ``window.location.hostname`` for comparison. */
export function normalizeHostname(hostname: string): string {
  return hostname.trim().toLowerCase().replace(/\.$/, "");
}

/**
 * Loopback IP literals. An IP cannot carry a tenant subdomain, so a page
 * served from one has to borrow ``localhost`` to address a sibling tenant
 * — ``127.0.0.1`` and ``<slug>.localhost`` are the same machine.
 *
 * Deliberately loopback-only: a deployment served from a routable IP is
 * not "local", and pointing it at ``localhost`` would send the user to
 * their own machine.
 */
function isLoopbackLiteral(host: string): boolean {
  return host === "[::1]" || /^127\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(host);
}

/**
 * Je hostname IP adresa (jakákoli), a ne jméno?
 *
 * Odlišné od ``isLoopbackLiteral`` výš, a schválně: ten odpovídá na
 * „běžím na tomhle stroji?", tenhle na „dá se tenhle host vůbec rozdělit
 * na ``<slug>.<apex>``?". U IP je odpověď ne — tečky v ní oddělují
 * oktety, ne DNS návěští.
 *
 * Bez tohohle rozlišení propadla ``127.0.0.1`` testem „aspoň tři návěští"
 * a její první oktet ``127`` i regexem na tvar slugu (číslice do
 * ``[a-z0-9-]`` patří), takže se IP tvářila jako tenant na neznámém
 * apexu — viz KAN-240.
 */
export function isIpLiteral(hostname: string): boolean {
  const host = normalizeHostname(hostname);
  // IPv6 chodí z ``window.location.hostname`` v hranatých závorkách.
  if (host.startsWith("[") || host.includes(":")) return true;
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(host);
}

/**
 * The apex a hostname belongs to, or ``null`` when it belongs to none.
 *
 * ``null`` means "this host is not part of a tenant-subdomain zone we know
 * how to build within" (a Vercel preview, an unrecognised custom domain,
 * SSR with no ``window``) — callers fall back to their configured default
 * rather than guessing.
 */
export function apexFromHostname(hostname: string): string | null {
  const host = normalizeHostname(hostname);
  const match = TENANT_APEX_DOMAINS.find(
    (apex) => host === apex || host.endsWith(`.${apex}`),
  );
  if (match) return match;
  if (isLoopbackLiteral(host)) return "localhost";
  return null;
}

/**
 * Scheme + apex + port of the deployment the page is currently served
 * from, as an origin prefix ready to have a host label prepended.
 *
 * This is what keeps "switch tenant" inside the deployment you are in:
 * local stays local (``http:``, ``:5174``), production stays production
 * (``https:``, no port). Returns ``null`` off-deployment.
 */
export function currentApexOrigin(): { protocol: string; apex: string; port: string } | null {
  if (typeof window === "undefined" || !window.location) return null;
  const apex = apexFromHostname(window.location.hostname);
  if (!apex) return null;
  return {
    protocol: window.location.protocol,
    apex,
    port: window.location.port,
  };
}

/**
 * Scheme + port of the current deployment, paired with the apex of the
 * requested KIND rather than of the page we happen to be on.
 *
 * This is what keeps a cross-zone link inside the same deployment once
 * the zones differ. Standing on ``acme.forgmatic.app`` and linking to
 * the marketing site must produce ``forgmatic.com``, not
 * ``forgmatic.app`` — and on a dev server it must stay on
 * ``:5174``/``http:`` rather than jumping to production.
 *
 * ``null`` when the current host belongs to no known apex (a Vercel
 * preview, an unrecognised custom domain, SSR): callers fall back to
 * their configured default instead of guessing.
 */
export function apexOriginOfKind(
  kind: "tenant" | "platform",
): { protocol: string; apex: string; port: string } | null {
  if (typeof window === "undefined" || !window.location) return null;
  const host = normalizeHostname(window.location.hostname);
  const list = kind === "tenant" ? TENANT_APEX_DOMAINS : PLATFORM_APEX_DOMAINS;

  // Prefer the apex the page already sits under — on an unsplit deploy
  // both lists carry it, and this keeps dev hosts (``*.localhost``)
  // resolving to the exact zone the cookie was scoped to.
  const own = list.find((apex) => host === apex || host.endsWith(`.${apex}`));
  if (own) {
    return {
      protocol: window.location.protocol,
      apex: own,
      port: window.location.port,
    };
  }

  // A loopback IP carries no labels, so it cannot belong to an apex —
  // it borrows the bare local one to address a sibling. Same rule as
  // ``apexFromHostname``; without it this falls through below and picks
  // the LONGEST local apex, turning ``127.0.0.1`` into
  // ``tycho.forgmatic.localhost`` instead of ``tycho.localhost``.
  if (isLoopbackLiteral(host) && list.includes("localhost")) {
    return {
      protocol: window.location.protocol,
      apex: "localhost",
      port: window.location.port,
    };
  }

  const isLocal = host.endsWith("localhost") || isLoopbackLiteral(host);

  // Local hosts that belong to no apex of the requested kind fall back
  // to their OWN apex — locally the platform is the bare apex and a
  // tenant is a label under it, so ``stark.localhost`` → ``localhost``.
  //
  // Bare ``localhost`` is a TENANT apex and deliberately not a platform
  // one, so without this branch asking for the platform origin from a
  // local tenant host would jump to ``forgmatic.localhost`` — a
  // different origin, whose host-only cookie the user would sign in
  // against and then come back without.
  if (isLocal) {
    const localApex = apexFromHostname(host);
    if (localApex) {
      return {
        protocol: window.location.protocol,
        apex: localApex,
        port: window.location.port,
      };
    }
  }

  // Split zones on a real deployment: we are standing in the other
  // one. Pair this deployment's scheme/port with the first entry of
  // the requested list.
  const target = list.find((apex) => apex.endsWith("localhost") === isLocal);
  if (!target) return null;
  return {
    protocol: window.location.protocol,
    apex: target,
    port: window.location.port,
  };
}
